/**
 * Validated once at module load, so a missing variable fails loudly at boot with a
 * readable message instead of surfacing as `undefined` inside a fetch URL later.
 */
function required(name: string, value: string | undefined): string {
  if (!value || value.trim().length === 0) {
    throw new Error(
      `Missing ${name}. Copy apps/web/.env.example to .env.local and fill it in.`,
    );
  }
  return value.trim();
}

const API_VERSION = '/v1';

/**
 * The base must carry the API VERSION, and must not be assumed to carry anything else.
 *
 * Behind the company proxy it is https://host/api/v1 — nginx strips `/api`, so the
 * server itself only ever sees /v1. Straight to the API it is http://host:3000/v1, with
 * no /api anywhere. Checking the version and nothing more is what lets one variable
 * describe both without the app needing to know a proxy exists.
 */
function versionedBase(value: string): string {
  const url = value.replace(/\/+$/, '');
  if (!url.endsWith(API_VERSION)) {
    throw new Error(
      `VITE_API_URL must end with ${API_VERSION} (got "${url}"). Behind a proxy that is ` +
        `usually https://host/api/v1; straight to the API it is http://host:3000/v1.`,
    );
  }
  return url;
}

export const env = {
  supabaseUrl: required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL),
  supabaseAnonKey: required('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY),
  /**
   * The VERSIONED base — it must end in /api/v1, because every call is written
   * relative to it.
   *
   * Checked rather than assumed: a base missing the suffix produces a 404 on every
   * request that looks exactly like a broken deploy, and finding that out from a
   * startup message beats finding it out from the network tab.
   */
  apiUrl: versionedBase(required('VITE_API_URL', import.meta.env.VITE_API_URL)),
  allowedEmailDomain: required(
    'VITE_ALLOWED_EMAIL_DOMAIN',
    import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN,
  ),
} as const;
